import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, Save, X, Palette, GripVertical, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { leadSettingsAPI } from '../lib/api';
import { usePermissions } from '../contexts/PermissionContext';

const SEMANTIC_STAGE_TYPES = [
  { value: 'entry', label: 'Entry', desc: 'First stage (New Lead, Inquiry)' },
  { value: 'engaged', label: 'Engaged', desc: 'Contact made (Contacted, Site Visit)' },
  { value: 'qualified', label: 'Qualified', desc: 'Lead qualified (Demo Done, Consultation)' },
  { value: 'proposal', label: 'Proposal', desc: 'Offer sent (Quote, Treatment Plan)' },
  { value: 'negotiation', label: 'Negotiation', desc: 'Active negotiation (Booking, Trial)' },
  { value: 'won', label: 'Won', desc: 'Deal closed (Registered, Enrolled)' },
  { value: 'lost', label: 'Lost', desc: 'Deal lost (Dropped, Cancelled)' },
  { value: 'custom', label: 'Custom', desc: 'No special meaning' },
];

// Default color palette for new stages
const DEFAULT_STAGE_COLORS = [
  '#3b82f6', // Blue
  '#8b5cf6', // Violet
  '#06b6d4', // Cyan
  '#f59e0b', // Amber
  '#f97316', // Orange
  '#10b981', // Emerald
  '#ef4444', // Red
  '#ec4899', // Pink
  '#6366f1', // Indigo
  '#14b8a6', // Teal
];

// Predefined color options for color picker
const COLOR_OPTIONS = [
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Violet', value: '#8b5cf6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Slate', value: '#64748b' },
  { name: 'Rose', value: '#f43f5e' },
];

export default function LeadSettings() {
  const { isAdmin } = usePermissions();
  
  // Sources
  const [sources, setSources] = useState([]);
  const [loadingSources, setLoadingSources] = useState(true);
  
  // Stages (for pipeline)
  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(true);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  // Form states
  const [newSource, setNewSource] = useState({ name: '', description: '' });
  const [newStage, setNewStage] = useState({ name: '', description: '', color: '#3b82f6' });

  const [editingSource, setEditingSource] = useState(null);
  const [editingStage, setEditingStage] = useState(null);

  // Load data on mount
  useEffect(() => {
    loadSources();
    loadStages();
  }, []);

  const loadSources = async () => {
    try {
      setLoadingSources(true);
      const response = await leadSettingsAPI.getSources();
      setSources(response.data || []);
    } catch (error) {
      console.error('Failed to load sources:', error);
      toast.error('Failed to load sources');
    } finally {
      setLoadingSources(false);
    }
  };

  const loadStages = async () => {
    try {
      setLoadingStages(true);
      const response = await leadSettingsAPI.getStages();
      // Sort stages by order
      const sortedStages = (response.data || []).sort((a, b) => (a.order || 0) - (b.order || 0));
      setStages(sortedStages);
    } catch (error) {
      console.error('Failed to load stages:', error);
      toast.error('Failed to load stages');
    } finally {
      setLoadingStages(false);
    }
  };

  // Source handlers
  const handleAddSource = async () => {
    if (!newSource.name.trim()) {
      toast.error('Please enter a source name');
      return;
    }
    
    // Check for duplicate source name (case-insensitive)
    const isDuplicate = sources.some(
      source => source.name.toLowerCase().trim() === newSource.name.toLowerCase().trim()
    );
    if (isDuplicate) {
      toast.error('A source with this name already exists');
      return;
    }
    
    try {
      await leadSettingsAPI.createSource({
        name: newSource.name,
        description: newSource.description
      });
      toast.success('Source added successfully');
      setNewSource({ name: '', description: '' });
      loadSources();
    } catch (error) {
      console.error('Failed to add source:', error);
      toast.error(error.response?.data?.detail || 'Failed to add source');
    }
  };

  const handleUpdateSource = async (id) => {
    if (!editingSource.name.trim()) {
      toast.error('Please enter a source name');
      return;
    }
    
    // Check for duplicate source name (case-insensitive), excluding the current source being edited
    const isDuplicate = sources.some(
      source => source.id !== id && source.name.toLowerCase().trim() === editingSource.name.toLowerCase().trim()
    );
    if (isDuplicate) {
      toast.error('A source with this name already exists');
      return;
    }
    
    try {
      await leadSettingsAPI.updateSource(id, {
        name: editingSource.name,
        description: editingSource.description
      });
      toast.success('Source updated successfully');
      setEditingSource(null);
      loadSources();
    } catch (error) {
      console.error('Failed to update source:', error);
      toast.error(error.response?.data?.detail || 'Failed to update source');
    }
  };

  const handleDeleteSource = async (id) => {
    if (window.confirm('Are you sure you want to delete this source?')) {
      try {
        await leadSettingsAPI.deleteSource(id);
        toast.success('Source deleted successfully');
        loadSources();
      } catch (error) {
        console.error('Failed to delete source:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete source');
      }
    }
  };

  // Stage handlers
  const handleAddStage = async () => {
    if (!newStage.name.trim()) {
      toast.error('Please enter a stage name');
      return;
    }
    
    // Check for duplicate stage name
    const isDuplicate = stages.some(
      stage => stage.name.toLowerCase().trim() === newStage.name.toLowerCase().trim()
    );
    if (isDuplicate) {
      toast.error('A stage with this name already exists');
      return;
    }
    
    try {
      // Calculate next order value (max order + 1)
      const maxOrder = stages.length > 0 
        ? Math.max(...stages.map(s => s.order || 0))
        : 0;
      
      await leadSettingsAPI.createStage({
        name: newStage.name,
        description: newStage.description,
        color: newStage.color,
        semantic_type: newStage.semantic_type || 'custom',
        is_won: newStage.semantic_type === 'won',
        is_lost: newStage.semantic_type === 'lost',
        order: maxOrder + 1
      });
      toast.success('Stage added successfully');
      // Set next default color for new stage
      const nextColorIndex = (stages.length + 1) % DEFAULT_STAGE_COLORS.length;
      setNewStage({ name: '', description: '', color: DEFAULT_STAGE_COLORS[nextColorIndex], semantic_type: 'custom' });
      loadStages();
    } catch (error) {
      console.error('Failed to add stage:', error);
      toast.error(error.response?.data?.detail || 'Failed to add stage');
    }
  };

  const handleUpdateStage = async (id) => {
    if (!editingStage.name.trim()) {
      toast.error('Please enter a stage name');
      return;
    }
    
    // Check for duplicate stage name, excluding current
    const isDuplicate = stages.some(
      stage => stage.id !== id && stage.name.toLowerCase().trim() === editingStage.name.toLowerCase().trim()
    );
    if (isDuplicate) {
      toast.error('A stage with this name already exists');
      return;
    }
    
    try {
      await leadSettingsAPI.updateStage(id, {
        name: editingStage.name,
        description: editingStage.description,
        color: editingStage.color,
        semantic_type: editingStage.semantic_type || 'custom',
        is_won: editingStage.semantic_type === 'won',
        is_lost: editingStage.semantic_type === 'lost',
        order: editingStage.order
      });
      toast.success('Stage updated successfully');
      setEditingStage(null);
      loadStages();
    } catch (error) {
      console.error('Failed to update stage:', error);
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    }
  };

  const handleToggleVisibility = async (stage) => {
    const next = stage.visible === false; // currently hidden -> show
    try {
      await leadSettingsAPI.setStageVisibility(stage.id, next);
      setStages(prev => prev.map(s => s.id === stage.id ? { ...s, visible: next } : s));
      toast.success(next ? `"${stage.name}" shown on board` : `"${stage.name}" hidden from board`);
    } catch (error) {
      console.error('Failed to toggle stage visibility:', error);
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    }
  };

  const handleDeleteStage = async (id) => {
    const stage = stages.find(s => s.id === id);
    if (stage?.is_system_default) {
      toast.error('Cannot delete system default stages');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this stage?')) {
      try {
        await leadSettingsAPI.deleteStage(id);
        toast.success('Stage deleted successfully');
        loadStages();
      } catch (error) {
        console.error('Failed to delete stage:', error);
        toast.error(error.response?.data?.detail || 'Failed to delete stage');
      }
    }
  };

  // Drag-and-drop reorder handlers (native HTML5 DnD — no extra dependency)
  const persistOrder = async (ordered) => {
    setSavingOrder(true);
    try {
      await leadSettingsAPI.reorderStages(ordered.map((s) => s.id));
      toast.success('Pipeline order saved');
    } catch (error) {
      console.error('Failed to reorder stages:', error);
      toast.error(error.response?.data?.detail || 'Could not save pipeline order');
      loadStages(); // revert to server truth
    } finally {
      setSavingOrder(false);
    }
  };

  const handleStageDrop = (targetIndex) => {
    setDragOverIndex(null);
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      return;
    }
    const reordered = [...stages];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setStages(reordered);
    setDragIndex(null);
    persistOrder(reordered);
  };

  // Color picker component
  const ColorPicker = ({ value, onChange, className = '' }) => (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex flex-wrap gap-1.5">
        {COLOR_OPTIONS.map((color) => (
          <button
            key={color.value}
            type="button"
            onClick={() => onChange(color.value)}
            className={`w-7 h-7 rounded-md border-2 transition-all hover:scale-110 ${
              value === color.value 
                ? 'border-foreground ring-2 ring-offset-2 ring-offset-background ring-primary' 
                : 'border-transparent hover:border-muted-foreground/30'
            }`}
            style={{ backgroundColor: color.value }}
            title={color.name}
          />
        ))}
      </div>
      <div className="flex items-center gap-1.5 ml-2">
        <Label className="text-xs text-muted-foreground">Custom:</Label>
        <Input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-10 h-8 p-0.5 cursor-pointer"
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Lead Settings</h1>
        <p className="text-muted-foreground mt-2">
          Manage sources and pipeline stages for your leads
        </p>
      </div>

      <Tabs defaultValue="stages" className="w-full">
        <TabsList>
          <TabsTrigger value="stages">Pipeline Stages</TabsTrigger>
          <TabsTrigger value="sources">Sources</TabsTrigger>
        </TabsList>

        {/* Stages Tab */}
        <TabsContent value="stages" className="space-y-4">
          {isAdmin() && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Add New Pipeline Stage
                </CardTitle>
                <CardDescription>
                  Create custom stages for your sales pipeline. Stages will be added at the end of the pipeline.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Stage Name *</Label>
                    <Input
                      value={newStage.name}
                      onChange={(e) => setNewStage({ ...newStage, name: e.target.value })}
                      placeholder="e.g., Discovery Call"
                      className="mt-1.5"
                      data-testid="stage-name-input"
                    />
                  </div>
                  <div>
                    <Label>Stage Type</Label>
                    <Select
                      value={newStage.semantic_type || 'custom'}
                      onValueChange={(v) => setNewStage({ ...newStage, semantic_type: v })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="stage-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {SEMANTIC_STAGE_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label} — {t.desc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Description (optional)</Label>
                  <Input
                    value={newStage.description}
                    onChange={(e) => setNewStage({ ...newStage, description: e.target.value })}
                    placeholder="Brief description of this stage"
                    className="mt-1.5"
                  />
                </div>
                
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Palette className="h-4 w-4" />
                    Stage Color
                  </Label>
                  <ColorPicker 
                    value={newStage.color} 
                    onChange={(color) => setNewStage({ ...newStage, color })}
                  />
                </div>
                
                <Button onClick={handleAddStage} className="w-full md:w-auto">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stage
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Pipeline Stages</CardTitle>
              <CardDescription>
                Drag to reorder stages. Leads progress through these stages from top to bottom.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingStages ? (
                <div className="space-y-3">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="h-14 bg-muted animate-pulse rounded-lg" />
                  ))}
                </div>
              ) : stages.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center mx-auto mb-3">
                    <Palette className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">No stages yet</p>
                  <p className="text-xs text-muted-foreground">Add your first pipeline stage above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {stages.map((stage, index) => {
                    const canDrag = isAdmin() && editingStage?.id !== stage.id;
                    return (
                    <div
                      key={stage.id}
                      draggable={canDrag}
                      onDragStart={() => canDrag && setDragIndex(index)}
                      onDragOver={(e) => { if (canDrag) { e.preventDefault(); setDragOverIndex(index); } }}
                      onDragLeave={() => dragOverIndex === index && setDragOverIndex(null)}
                      onDrop={(e) => { e.preventDefault(); handleStageDrop(index); }}
                      onDragEnd={() => { setDragIndex(null); setDragOverIndex(null); }}
                      data-testid={`stage-row-${index}`}
                      className={`flex items-center gap-3 p-3 border rounded-lg bg-card transition-colors ${
                        dragOverIndex === index ? 'border-primary ring-1 ring-primary/40 bg-muted/60' : 'hover:bg-muted/50'
                      } ${dragIndex === index ? 'opacity-50' : ''}`}
                    >
                      {editingStage?.id === stage.id ? (
                        <>
                          <div className="flex-1 space-y-3">
                            <Input
                              value={editingStage.name}
                              onChange={(e) =>
                                setEditingStage({ ...editingStage, name: e.target.value })
                              }
                              placeholder="Stage name"
                            />
                            <ColorPicker 
                              value={editingStage.color || '#3b82f6'} 
                              onChange={(color) => setEditingStage({ ...editingStage, color })}
                            />
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStage(stage.id)}
                            >
                              <Save className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingStage(null)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Drag handle */}
                          {isAdmin() && (
                            <div
                              className="cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground shrink-0"
                              title="Drag to reorder"
                              data-testid={`stage-drag-handle-${index}`}
                            >
                              <GripVertical className="h-4 w-4" />
                            </div>
                          )}
                          {/* Stage Order Number */}
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold shrink-0"
                            style={{ backgroundColor: stage.color || '#3b82f6' }}
                          >
                            {index + 1}
                          </div>
                          
                          {/* Stage Name */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-foreground">{stage.name}</p>
                            {stage.description && (
                              <p className="text-xs text-muted-foreground truncate">{stage.description}</p>
                            )}
                          </div>
                          
                          {/* Color Preview */}
                          <div 
                            className="w-6 h-6 rounded-md border border-border shrink-0"
                            style={{ backgroundColor: stage.color || '#3b82f6' }}
                            title={`Color: ${stage.color || '#3b82f6'}`}
                          />
                          
                          {/* Badges */}
                          {stage.semantic_type && stage.semantic_type !== 'custom' && (
                            <Badge variant="outline" className="text-xs capitalize">
                              {stage.semantic_type}
                            </Badge>
                          )}
                          {stage.is_won && (
                            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs">
                              Won Stage
                            </Badge>
                          )}
                          {stage.is_lost && (
                            <Badge className="bg-red-500/10 text-red-600 border-red-500/20 text-xs">
                              Lost Stage
                            </Badge>
                          )}
                          {stage.is_system_default && (
                            <Badge variant="secondary" className="text-xs">
                              Default
                            </Badge>
                          )}
                          {stage.visible === false && (
                            <Badge variant="outline" className="text-xs text-muted-foreground border-muted-foreground/40">
                              Hidden
                            </Badge>
                          )}
                          
                          {/* Actions */}
                          {isAdmin() && !(stage.is_won || stage.is_lost) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`stage-visibility-toggle-${stage.id}`}
                              title={stage.visible === false ? 'Show on board' : 'Hide from board'}
                              onClick={() => handleToggleVisibility(stage)}
                            >
                              {stage.visible === false ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4" />}
                            </Button>
                          )}
                          {!stage.is_system_default && isAdmin() && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingStage({ ...stage, color: stage.color || '#3b82f6' })}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteStage(stage.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sources Tab */}
        <TabsContent value="sources" className="space-y-4">
          {isAdmin() && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Add New Source
                </CardTitle>
                <CardDescription>
                  Track where your leads come from by adding custom sources.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Source Name *</Label>
                    <Input
                      value={newSource.name}
                      onChange={(e) => setNewSource({ ...newSource, name: e.target.value })}
                      placeholder="e.g., LinkedIn, Trade Show, Referral"
                      className="mt-1.5"
                    />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={handleAddSource} className="w-full">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Source
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Lead Sources</CardTitle>
              <CardDescription>
                Sources help you understand which channels bring in the most leads.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingSources ? (
                <div className="space-y-3">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-12 bg-muted animate-pulse rounded-lg" />
                  ))}
                </div>
              ) : sources.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-sm font-medium text-muted-foreground mb-1">No sources yet</p>
                  <p className="text-xs text-muted-foreground">Add your first lead source above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {sources.map((source) => (
                    <div
                      key={source.id}
                      className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      {editingSource?.id === source.id ? (
                        <>
                          <Input
                            value={editingSource.name}
                            onChange={(e) =>
                              setEditingSource({ ...editingSource, name: e.target.value })
                            }
                            className="flex-1"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleUpdateSource(source.id)}
                          >
                            <Save className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setEditingSource(null)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 font-medium">{source.name}</span>
                          {source.is_system_default && (
                            <Badge variant="secondary" className="text-xs">
                              Default
                            </Badge>
                          )}
                          {!source.is_system_default && isAdmin() && (
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setEditingSource(source)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteSource(source.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
